import axiosInstance from "../utils/axios.config";
import logger from "../utils/logger";
import { CASE_ERRORS } from "../constants/errorMessages"; // Fixed path

export const dashboardService = {
  async getStats() {
    try {
      const response = await axiosInstance.get("/dashboard/stats");
      return response.data;
    } catch (error) {
      logger.error("Error fetching dashboard stats:", error);
      throw new Error("فشل في جلب إحصائيات لوحة التحكم");
    }
  },

  async getRecentCases() {
    try {
      const response = await axiosInstance.get("/dashboard/recent-cases");
      return response.data;
    } catch (error) {
      logger.error("Error fetching recent cases:", error);
      throw new Error("فشل في جلب آخر القضايا");
    }
  },

  async getUpcomingEvents() {
    try {
      const response = await axiosInstance.get("/dashboard/upcoming-events");
      return response.data;
    } catch (error) {
      logger.error("Error fetching upcoming events:", error);
      throw new Error("فشل في جلب المواعيد القادمة");
    }
  },
};

export const casesService = {
  async getCases() {
    try {
      const response = await axiosInstance.get("/cases");
      return response.data.data || []; // تعامل مباشرة مع البيانات من المصفوفة
    } catch (error) {
      logger.error("Error fetching cases:", error);
      throw new Error(CASE_ERRORS.FETCH_ERROR);
    }
  },

  async getCaseById(id) {
    try {
      const response = await axiosInstance.get(`/cases/${id}`);
      return response.data.data;
    } catch (error) {
      logger.error("Error fetching case by ID:", error);
      throw new Error(CASE_ERRORS.FETCH_CASE_ERROR);
    }
  },

  async getCaseDetails(id) {
    try {
      const caseResponse = await axiosInstance.get(`/cases/${id}`);
      const caseData = caseResponse.data.data;

      return {
        ...caseData,
        // احتفظ بالتواريخ الأصلية
        filing_date_original: caseData.filing_date,
        next_session_date_original: caseData.next_session_date,
        // التواريخ المنسقة للعرض
        filing_date: caseData.filing_date
          ? new Date(caseData.filing_date).toLocaleDateString("ar", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "غير محدد",
        next_session_date: caseData.next_session_date
          ? new Date(caseData.next_session_date).toLocaleDateString("ar", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "لا يوجد",
      };
    } catch (error) {
      logger.error("Error fetching case details:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب تفاصيل القضية"
      );
    }
  },

  async updateCase(id, caseData) {
    try {
      const formattedData = {
        ...caseData,
        description: caseData.description?.trim() || null,
        notes: caseData.notes?.trim() || null,
        court_case_number: caseData.court_case_number?.trim() || null,
        // filing_date: caseData.filing_date ? moment(caseData.filing_date).format('YYYY-MM-DD') : null,
        // next_session_date: caseData.next_session_date ? moment(caseData.next_session_date).format('YYYY-MM-DD') : null
      };

      logger.debug("Updating case with formatted data:", formattedData);
      const response = await axiosInstance.put(`/cases/${id}`, formattedData);
      return response.data;
    } catch (error) {
      logger.error("Error updating case:", error);
      throw new Error(error.response?.data?.message || "فشل في تحديث القضية");
    }
  },

  async createCase(caseData) {
    try {
      // تأكد من وجود الحقول المطلوبة
      if (
        !caseData.case_number ||
        !caseData.client_name ||
        !caseData.case_type
      ) {
        throw new Error("الحقول المطلوبة: رقم القضية، اسم العميل، نوع القضية");
      }

      const formattedData = {
        case_number: caseData.case_number?.trim(),
        title: caseData.title?.trim() || null,
        court_case_number: caseData.court_case_number?.trim() || null,
        client_id: caseData.client_id || null,
        client_name: caseData.client_name?.trim(),
        case_type: caseData.case_type?.trim(),
        description: caseData.description?.trim() || null,
        opponent_id: caseData.opponent_id || null,
        opponent_name: caseData.opponent_name?.trim() || null,
        court_name: caseData.court_name?.trim() || null,
        status: caseData.status || "جديدة",
        filing_date:
          caseData.filing_date || new Date().toISOString().split("T")[0],
        next_session_date: caseData.next_session_date || null,
        notes: caseData.notes?.trim() || null,
        agreed_fees: caseData.agreed_fees || null,
      };

      logger.debug("Sending data to server:", formattedData);
      const response = await axiosInstance.post("/cases", formattedData);
      return response.data;
    } catch (error) {
      logger.error("Error creating case:", error);

      // تحسين رسالة الخطأ للأرقام المكررة
      if (
        error.response?.data?.message?.includes("Duplicate entry") &&
        error.response?.data?.message?.includes("case_number")
      ) {
        throw new Error("رقم القضية مكرر. يرجى المحاولة مرة أخرى.");
      }

      throw new Error(error.response?.data?.message || "فشل في إنشاء القضية");
    }
  },

  async deleteCase(id) {
    try {
      logger.debug("Attempting to delete case:", id);
      const response = await axiosInstance.delete(`/cases/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في حذف القضية");
      }

      return response.data;
    } catch (error) {
      logger.error("Error deleting case:", error);
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "حدث خطأ أثناء حذف القضية"
      );
    }
  },
};

export const formatDate = (date) => {
  if (!date) return null;
  try {
    return new Date(date).toLocaleDateString("ar", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    logger.error("Date formatting error:", error);
    return null;
  }
};

// خدمة العملاء
export const clientsService = {
  // جلب جميع العملاء
  async getClients() {
    try {
      const response = await axiosInstance.get("/clients");
      return response.data.data || [];
    } catch (error) {
      logger.error("Error fetching clients:", error);
      throw new Error("فشل في جلب العملاء");
    }
  },

  // جلب عميل بالمعرف
  async getClientById(id) {
    try {
      const response = await axiosInstance.get(`/clients/${id}`);
      return response.data.data;
    } catch (error) {
      logger.error("Error fetching client:", error);
      throw new Error("فشل في جلب بيانات العميل");
    }
  },

  // إنشاء عميل جديد
  async createClient(clientData) {
    try {
      // التحقق من البيانات المطلوبة
      if (!clientData.name || !clientData.phone) {
        throw new Error("اسم العميل ورقم الهاتف مطلوبان");
      }

      const formattedData = {
        name: clientData.name?.trim(),
        phone: clientData.phone?.trim(),
        email: clientData.email?.trim() || null,
        address: clientData.address?.trim() || null,
        national_id: clientData.national_id?.trim() || null,
        type: clientData.type || "individual",
        client_type: clientData.client_type || "مدعي",
        power_of_attorney_number: clientData.power_of_attorney_number?.trim() || null,
        power_of_attorney_record: clientData.power_of_attorney_record?.trim() || null,
        power_of_attorney_date: clientData.power_of_attorney_date ? 
          clientData.power_of_attorney_date.format('YYYY-MM-DD') : null,
        notary_name: clientData.notary_name?.trim() || null,
      };

      const response = await axiosInstance.post("/clients", formattedData);
      return response.data;
    } catch (error) {
      logger.error("Error creating client:", error);
      throw new Error(error.response?.data?.message || "فشل في إنشاء العميل");
    }
  },

  // تحديث بيانات العميل
  async updateClient(id, clientData) {
    try {
      const formattedData = {
        name: clientData.name?.trim(),
        phone: clientData.phone?.trim(),
        email: clientData.email?.trim() || null,
        address: clientData.address?.trim() || null,
        national_id: clientData.national_id?.trim() || null,
        type: clientData.type || "individual",
        agency_info: clientData.agency_info?.trim() || null,
        power_of_attorney_record: clientData.power_of_attorney_record?.trim() || null,
      };
      const response = await axiosInstance.put(`/clients/${id}`, formattedData);
      return response.data;
    } catch (error) {
      logger.error("Error updating client:", error);
      throw new Error(
        error.response?.data?.message || "فشل في تحديث بيانات العميل"
      );
    }
  },

  // حذف العميل
  async deleteClient(id) {
    try {
      const response = await axiosInstance.delete(`/clients/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error deleting client:", error);
      throw new Error(error.response?.data?.message || "فشل في حذف العميل");
    }
  },
};

export const authService = {
  async login(credentials, remember = false) {
    try {
      const response = await axiosInstance.post("/auth/login", credentials);

      logger.debug("Server response:", response.data); // للتأكد من الاستجابة

      if (response.data && response.data.token) {
        // تحضير بيانات المستخدم للتخزين مع معلومات المستأجر
        const userToStore = {
          ...response.data.user,
          tenant: response.data.tenant
        };

        // حفظ التوكن وبيانات المستخدم
        if (remember) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(userToStore));
        } else {
          sessionStorage.setItem("token", response.data.token);
          sessionStorage.setItem("user", JSON.stringify(userToStore));
        }

        // تعيين التوكن في رأس الطلبات
        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.token}`;

        return {
          success: true,
          data: response.data,
        };
      }

      return { success: false, message: "فشل تسجيل الدخول" };
    } catch (error) {
      logger.error("Auth error:", error);

      // إذا كان الخطأ 403، نعيد كائن الخطأ كاملاً للتعامل معه في Login.js
      if (error.response && error.response.status === 403) {
        throw error;
      }

      throw new Error(error.response?.data?.message || "فشل تسجيل الدخول");
    }
  },

  async register(userData) {
    try {
      // تعيين الدور كمحامي
      const userDataWithRole = {
        ...userData,
        role: "lawyer", // تعيين الدور كمحامي
      };
      const response = await axiosInstance.post(
        "/auth/register",
        userDataWithRole
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      logger.error("Registration error:", error);
      throw new Error(error.response?.data?.message || "فشل في إنشاء الحساب");
    }
  },

  async setTokenAndInit(token, remember = false) {
    // حفظ التوكن
    if (remember) {
      localStorage.setItem("token", token);
    } else {
      sessionStorage.setItem("token", token);
    }

    // تهيئة الـ axios
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    // التحقق من صحة التوكن
    try {
      const verifyResponse = await axiosInstance.get("/auth/verify");
      return verifyResponse.data.success;
    } catch (error) {
      this.clearTokens();
      throw new Error("فشل التحقق من صحة التوكن");
    }
  },

  async autoLogin() {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");

    if (!token) {
      return { success: false };
    }

    // Prevent verification if already on login page
    if (window.location.pathname === "/login") {
      return { success: false };
    }

    try {
      const response = await axiosInstance.get("/auth/verify", {
        // Add cache control
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (response.data.success) {
        // تحديث بيانات المستخدم المخزنة لتشمل معلومات المستأجر الأحدث
        const userToStore = {
          ...response.data.user,
          tenant: response.data.tenant
        };
        
        // نحدث التخزين المستخدم حالياً (سواء local أو session)
        if (localStorage.getItem("token")) {
          localStorage.setItem("user", JSON.stringify(userToStore));
        } else if (sessionStorage.getItem("token")) {
           sessionStorage.setItem("user", JSON.stringify(userToStore));
        }

        return { success: true, token };
      }

      this.clearTokens();
      return { success: false };
    } catch (error) {
      this.clearTokens();
      return { success: false };
    }
  },

  clearTokens() {
    sessionStorage.removeItem("token");
    localStorage.removeItem("token");
    delete axiosInstance.defaults.headers.common["Authorization"];
  },

  logout() {
    this.clearTokens();
    // استخدام window.location.replace لتجنب مشاكل التوجيه
    if (window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
  },

  checkAuth() {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");
    return !!token;
  },
};

export const sessionsService = {
  // جلب جميع الجلسات
  async getAllSessions() {
    try {
      const response = await axiosInstance.get("/sessions");
      return response.data.data || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || "فشل في جلب الجلسات");
    }
  },

  // جلب جلسات قضية محددة
  async getSessionsByCase(caseId) {
    try {
      const response = await axiosInstance.get(`/sessions/case/${caseId}`);
      return response.data.data || [];
    } catch (error) {
      logger.error("Sessions fetch error:", error);
      throw new Error(error.response?.data?.message || "فشل في جلب الجلسات");
    }
  },

  // جلب جلسات عميل محدد
  async getSessionsByClient(clientId) {
    try {
      const response = await axiosInstance.get(`/sessions/client/${clientId}`);
      return response.data.data || [];
    } catch (error) {
      logger.error("Client sessions fetch error:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب جلسات العميل"
      );
    }
  },

  // إضافة جلسة جديدة
  async addSession(sessionData) {
    try {
      if (!sessionData) {
        throw new Error("بيانات الجلسة غير متوفرة");
      }
      // التأكد من صحة التاريخ والوقت
      if (!sessionData.date || !sessionData.time) {
        throw new Error("التاريخ والوقت مطلوبان");
      }

      // تنسيق البيانات
      const formattedData = {
        ...sessionData,
        // تغيير تنسيق التاريخ إلى YYYY-MM-DD
        //   date: moment(sessionData.date).format('YYYY-MM-DD'),
        // التأكد من تنسيق الوقت بشكل صحيح
        //   time: moment(sessionData.time).format('HH:mm:ss'),
        title: sessionData.title?.trim(),
        court: sessionData.court?.trim() || null,
        room: sessionData.room?.trim() || null,
        judge: sessionData.judge?.trim() || null,
        decisions: sessionData.decisions?.trim() || null,
        notes: sessionData.notes?.trim() || null,
      };

      logger.debug("Sending formatted data to server:", formattedData);
      const response = await axiosInstance.post("/sessions", formattedData);

      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في إضافة الجلسة");
      }
      return response.data.data;
    } catch (error) {
      logger.error("Error adding session:", error);
      throw new Error(
        error.response?.data?.message || "حدث خطأ في إضافة الجلسة"
      );
    }
  },

  // تحديث جلسة
  async updateSession(sessionId, sessionData) {
    try {
      const response = await axiosInstance.put(
        `/sessions/${sessionId}`,
        sessionData
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في تحديث الجلسة");
      }
      return response.data.data;
    } catch (error) {
      logger.error("Error updating session:", error);
      throw new Error(
        error.response?.data?.message || "حدث خطأ في تحديث الجلسة"
      );
    }
  },

  // حذف جلسة
  async deleteSession(sessionId) {
    try {
      const response = await axiosInstance.delete(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "حدث خطأ في حذف الجلسة");
    }
  },

  // جلب تفاصيل جلسة محددة
  async getSessionById(sessionId) {
    try {
      const response = await axiosInstance.get(`/sessions/${sessionId}`);
      return response.data.data || response.data;
    } catch (error) {
      logger.error("Error fetching session details:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب تفاصيل الجلسة"
      );
    }
  },

  // جلب جلسات اليوم
  async getTodaySessions() {
    try {
      // استخدام واجهة برمجة التطبيقات للمواعيد القادمة مع فلترة التاريخ
      const response = await axiosInstance.get("/dashboard/upcoming-events");
      const allEvents = response.data || [];

      // الحصول على تاريخ اليوم
      const today = new Date();
      const todayString = today.toISOString().split("T")[0];
      logger.debug("Today's date:", todayString);
      logger.debug("All events:", allEvents);

      // فلترة الأحداث لعرض أحداث اليوم فقط
      const todayEvents = allEvents.filter((event) => {
        // التحقق من وجود تاريخ
        if (!event.date && !event.session_date) return false;

        // الحصول على تاريخ الحدث
        const eventDate = event.date || event.session_date;

        // تحويل التاريخ إلى كائن تاريخ للمقارنة
        const eventDateObj = new Date(eventDate);
        const eventDateString = eventDateObj.toISOString().split("T")[0];

        // مقارنة التواريخ
        return eventDateString === todayString;
      });

      // تحسين بيانات الجلسات مع جلب معلومات إضافية
      const enhancedTodayEvents = await Promise.all(
        todayEvents.map(async (event) => {
          // إنشاء نسخة من الحدث لتجنب تعديل الكائن الأصلي
          const enhancedEvent = { ...event };

          // جلب تفاصيل الجلسة الكاملة إذا كان لدينا معرف
          if (event.id) {
            try {
              const sessionDetails = await this.getSessionById(event.id);
              // دمج البيانات مع إعطاء الأولوية للبيانات المفصلة
              Object.keys(sessionDetails).forEach((key) => {
                if (
                  sessionDetails[key] !== undefined &&
                  sessionDetails[key] !== null
                ) {
                  enhancedEvent[key] = sessionDetails[key];
                }
              });
            } catch (error) {
              logger.error(
                `Error fetching session details for session ${event.id}:`,
                error
              );
            }
          }

          // التأكد من وجود الحقول الأساسية
          if (!enhancedEvent.client_name) {
            enhancedEvent.client_name =
              enhancedEvent.case?.client_name ||
              enhancedEvent.case?.client ||
              enhancedEvent.case?.clients?.[0]?.name ||
              enhancedEvent.clients?.[0]?.name ||
              enhancedEvent.parties?.[0]?.name ||
              "عميل غير محدد";
          }

          if (!enhancedEvent.session_type) {
            enhancedEvent.session_type =
              enhancedEvent.case?.session_type ||
              enhancedEvent.case?.type ||
              "جلسة قضائية";
          }

          if (!enhancedEvent.case_number && enhancedEvent.case?.case_number) {
            enhancedEvent.case_number = enhancedEvent.case.case_number;
          }

          if (!enhancedEvent.court && enhancedEvent.case?.court_name) {
            enhancedEvent.court = enhancedEvent.case.court_name;
          }

          if (!enhancedEvent.room && enhancedEvent.case?.court_room) {
            enhancedEvent.room = enhancedEvent.case.court_room;
          }

          if (!enhancedEvent.judge && enhancedEvent.case?.judge_name) {
            enhancedEvent.judge = enhancedEvent.case.judge_name;
          }

          if (!enhancedEvent.decisions && enhancedEvent.case?.last_decision) {
            enhancedEvent.decisions = enhancedEvent.case.last_decision;
          }

          if (!enhancedEvent.notes && enhancedEvent.case?.notes) {
            enhancedEvent.notes = enhancedEvent.case.notes;
          }

          // تحديد حالة الجلسة بناءً على الوقت
          if (!enhancedEvent.status) {
            const sessionTime =
              enhancedEvent.time || enhancedEvent.session_time;
            if (sessionTime) {
              const now = new Date();
              const [hours, minutes] = sessionTime.split(":").map(Number);
              const sessionDate = new Date(
                enhancedEvent.date || enhancedEvent.session_date || now
              );
              sessionDate.setHours(hours, minutes, 0, 0);

              if (sessionDate < now) {
                enhancedEvent.status = "منعقدة";
              } else {
                enhancedEvent.status = "قادمة";
              }
            } else {
              enhancedEvent.status = "قادمة";
            }
          }

          return enhancedEvent;
        })
      );

      // ترتيب الجلسات حسب الوقت
      enhancedTodayEvents.sort((a, b) => {
        const timeA = a.time || a.session_time || "00:00";
        const timeB = b.time || b.session_time || "00:00";
        return timeA.localeCompare(timeB);
      });

      logger.debug("Enhanced today's events:", enhancedTodayEvents);
      return enhancedTodayEvents;
    } catch (error) {
      logger.error("Error fetching today's sessions:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب جلسات اليوم"
      );
    }
  },
};

export const documentsService = {
  async getDocumentsByCaseId(caseId) {
    try {
      const response = await axiosInstance.get(`/cases/${caseId}/documents`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching documents:", error);
      throw error;
    }
  },

  async uploadDocument(caseId, file, documentDetails = {}, clientId = null) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", documentDetails.title || file.name);

      // إضافة تفاصيل المستند إذا كانت متوفرة
      if (documentDetails.document_type) {
        formData.append("document_type", documentDetails.document_type);
      }
      if (documentDetails.notes) {
        formData.append("notes", documentDetails.notes);
      }

      // إضافة معرف العميل إذا كان متوفرًا
      if (clientId) {
        formData.append("client_id", clientId);
      }

      const response = await axiosInstance.post(
        `/cases/${caseId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error("Error uploading document:", error);
      throw error;
    }
  },

  // دالة لرفع مستند لعميل مباشرة
  async uploadClientDocument(clientId, file, documentDetails = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", documentDetails.title || file.name);
      formData.append(
        "document_type",
        documentDetails.document_type || "مستند عام"
      );
      formData.append("notes", documentDetails.notes || "");
      formData.append("client_id", clientId);

      const response = await axiosInstance.post(
        `/documents/clients/${clientId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error("Error uploading document:", error);
      throw error;
    }
  },

  // دالة لجلب مستندات العميل
  async getClientDocuments(clientId) {
    try {
      const response = await axiosInstance.get(
        `/documents/clients/${clientId}`
      );
      return response.data;
    } catch (error) {
      logger.error("Error fetching client documents:", error);
      throw error;
    }
  },

  // دالة لحذف مستند العميل
  async deleteClientDocument(documentId) {
    try {
      const response = await axiosInstance.delete(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      logger.error("Error deleting client document:", error);
      throw error;
    }
  },

  // دالة لتحميل مستند العميل
  async downloadClientDocument(documentId, filename = null) {
    try {
      const response = await axiosInstance.get(
        `/documents/${documentId}/download`,
        {
          responseType: "blob",
        }
      );

      // إنشاء رابط التحميل
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // الحصول على اسم الملف من headers أو استخدام الافتراضي
      const contentDisposition = response.headers["content-disposition"];
      let downloadFilename = filename || `document-${documentId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename\*=UTF-8''(.+)/
        );
        if (filenameMatch) {
          downloadFilename = decodeURIComponent(filenameMatch[1]);
        }
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      logger.error("Error downloading client document:", error);
      throw new Error(error.response?.data?.message || "فشل في تحميل المستند");
    }
  },

  async deleteDocument(caseId, documentId) {
    try {
      const response = await axiosInstance.delete(
        `/cases/${caseId}/documents/${documentId}`
      );
      return response.data;
    } catch (error) {
      logger.error("Error deleting document:", error);
      throw error;
    }
  },

  // تحديث دالة تحميل مستند القضية
  async downloadDocument(caseId, documentId, filename = null) {
    try {
      const response = await axiosInstance.get(
        `/cases/${caseId}/documents/${documentId}/download`,
        {
          responseType: "blob",
        }
      );

      // إنشاء رابط التحميل
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // الحصول على اسم الملف من headers أو استخدام الافتراضي
      const contentDisposition = response.headers["content-disposition"];
      let downloadFilename = filename || `document-${documentId}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename\*=UTF-8''(.+)/
        );
        if (filenameMatch) {
          downloadFilename = decodeURIComponent(filenameMatch[1]);
        }
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      logger.error("Error downloading document:", error);
      throw new Error(error.response?.data?.message || "فشل في تحميل المستند");
    }
  },

  async getCaseDocuments(caseId) {
    try {
      const response = await axiosInstance.get(`/cases/${caseId}/documents`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching documents:", error);
      throw new Error("Failed to fetch documents");
    }
  },

  async searchDocuments(searchTerm) {
    try {
      const response = await axiosInstance.get(`/documents/search`, {
        params: { searchTerm },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "فشل في البحث عن المستندات"
      );
    }
  },

  async updateDocument(documentId, data) {
    try {
      const response = await axiosInstance.put(
        `/documents/${documentId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "فشل في تحديث المستند");
    }
  },

  // إضافة هذه الدالة إلى documentsService في api.service.js
  getDocumentViewUrl(docId) {
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseURL}/documents/${docId}/preview`;
  },

  async getDocumentDirectUrl(docId) {
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseURL}/uploads/documents/${docId}`;
  },

  getDocumentPreviewUrl(docId) {
    if (!docId) return "";
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseURL}/documents/${docId}/preview`;
  },

  getDocumentThumbnailUrl(docId, document_type) {
    if (!docId) return "";
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    // For image files, use the preview endpoint
    if (document_type?.toLowerCase().includes("image")) {
      return `${baseURL}/documents/${docId}/preview?thumbnail=true`;
    }

    // For non-image files, you could return a type-specific icon URL
    // or a placeholder image
    return null;
  },

  async previewDocument(documentId) {
    try {
      const response = await axiosInstance.get(
        `/documents/${documentId}/preview`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      logger.error("Error previewing document:", error);
      throw error;
    }
  },

  // دالة للحصول على رابط المعاينة المباشر
  getPreviewUrl(documentId) {
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    return `${baseURL}/documents/${documentId}/preview`;
  },
};

export const paymentsService = {
  async getPayments() {
    try {
      logger.debug("Calling payments API");
      const response = await axiosInstance.get("/payments");
      logger.debug("API response:", response);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const payments = response.data.data;
        logger.debug(`Retrieved ${payments.length} payments`);

        // التحقق من أن كل سجل لديه الحقول المطلوبة
        const validatedPayments = payments.map((payment) => ({
          id: payment.id,
          case_number: payment.case_number || "",
          client_name: payment.client_name || "",
          amount: parseFloat(payment.amount) || 0,
          date:
            payment.date || payment.payment_date || new Date().toISOString(),
          type: payment.type || payment.payment_type || "other",
          status: payment.status || "pending",
          description: payment.description || "",
        }));

        return {
          success: true,
          data: validatedPayments,
        };
      }

      throw new Error("البيانات المستلمة غير صالحة");
    } catch (error) {
      logger.error("Error fetching payments:", error);
      throw new Error(error.response?.data?.message || "فشل في جلب المدفوعات");
    }
  },

  async addPayment(paymentData) {
    try {
      const response = await axiosInstance.post("/payments", paymentData);
      return response.data;
    } catch (error) {
      logger.error("Error adding payment:", error);
      throw new Error(error.response?.data?.message || "فشل في إضافة المدفوع");
    }
  },

  async updatePayment(id, paymentData) {
    try {
      const response = await axiosInstance.put(`/payments/${id}`, paymentData);
      return response.data;
    } catch (error) {
      logger.error("Error updating payment:", error);
      throw new Error(error.response?.data?.message || "فشل في تحديث المدفوع");
    }
  },

  async deletePayment(id) {
    try {
      logger.debug(`Attempting to delete payment with ID: ${id}`);

      // إضافة تأخير اصطناعي لإظهار مؤشر التحميل (اختياري - للاختبار فقط)
      // await new Promise(resolve => setTimeout(resolve, 500));

      const response = await axiosInstance.delete(`/payments/${id}`);

      logger.debug("Delete response:", response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في حذف المدفوع");
      }

      return response.data;
    } catch (error) {
      logger.error("Error deleting payment:", error);
      throw new Error(error.response?.data?.message || "فشل في حذف المدفوع");
    }
  },

  async getPaymentsByCase(caseId) {
    try {
      logger.debug("Calling case payments API for case:", caseId);
      const response = await axiosInstance.get(`/payments/case/${caseId}`);
      logger.debug("Case payments API response:", response);

      // التعامل مع بنية الاستجابة المباشرة
      if (response && Array.isArray(response.data)) {
        const payments = response.data;
        logger.debug(`Retrieved ${payments.length} payments for case ${caseId}`);

        // التحقق من أن كل سجل لديه الحقول المطلوبة
        const validatedPayments = payments.map((payment) => ({
          id: payment.id,
          case_number: payment.case_number || "",
          client_name: payment.client_name || "",
          amount: parseFloat(payment.amount) || 0,
          date:
            payment.date || payment.payment_date || new Date().toISOString(),
          type: payment.type || payment.payment_type || "other",
          status: payment.status || "pending",
          description: payment.description || "",
        }));

        return {
          success: true,
          data: validatedPayments,
        };
      }

      throw new Error("البيانات المستلمة غير صالحة");
    } catch (error) {
      logger.error("Error fetching case payments:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب مدفوعات القضية"
      );
    }
  },
};

export const profileService = {
  async getProfile() {
    try {
      logger.debug("Fetching profile data...");
      const response = await axiosInstance.get("/profile/me"); // ✅ تصحيح المسار
      logger.debug("Profile data received:", response.data);

      if (!response.data.success) {
        throw new Error(
          response.data.message || "فشل في جلب بيانات الملف الشخصي"
        );
      }

      // ✅ إرجاع البيانات بالبنية الصحيحة
      const profileData = response.data.profile || response.data.data;
      
      // تحديث التخزين المحلي إذا توفرت معلومات المستأجر، لضمان عمل الهيدر
      if (response.data.tenant) {
        try {
           const storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
           if (storedUser) {
             const user = JSON.parse(storedUser);
             // دمج معلومات المستأجر
             const updatedUser = { ...user, tenant: response.data.tenant };
             
             if (localStorage.getItem("token")) {
               localStorage.setItem("user", JSON.stringify(updatedUser));
             } else {
               sessionStorage.setItem("user", JSON.stringify(updatedUser));
             }
           }
        } catch (e) {
          logger.error("Failed to update stored tenant data:", e);
        }
      }

      return {
        success: true,
        data: profileData,
      };
    } catch (error) {
      logger.error("Error fetching profile:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب بيانات الملف الشخصي"
      );
    }
  },

  async updateProfile(data) {
    try {
      const response = await axiosInstance.put("/profile/me", data);
      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في تحديث الملف الشخصي");
      }
      return response.data;
    } catch (error) {
      logger.error("Error updating profile:", error);
      throw new Error(
        error.response?.data?.message || "فشل في تحديث الملف الشخصي"
      );
    }
  },

  async uploadLogo(file) {
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await axiosInstance.post("/profile/me/logo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في رفع الشعار");
      }
      return response.data;
    } catch (error) {
      logger.error("Error uploading logo:", error);
      throw new Error(error.response?.data?.message || "فشل في رفع الشعار");
    }
  },

  async uploadProfileImage(file) {
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const response = await axiosInstance.post("/profile/me/logo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (!response.data.success) {
        throw new Error(response.data.message || "فشل في رفع الصورة الشخصية");
      }
      return response.data;
    } catch (error) {
      logger.error("Error uploading profile image:", error);
      throw new Error("فشل في رفع الصورة الشخصية");
    }
  },

  // ✅ إضافة دوال جديدة للبروفايل الموسع
  async getExtendedProfile() {
    try {
      const response = await axiosInstance.get("/profile/extended");
      return response.data;
    } catch (error) {
      logger.error("Error fetching extended profile:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب البيانات الموسعة"
      );
    }
  },

  async updateExtendedProfile(data) {
    try {
      const response = await axiosInstance.put("/profile/extended", data);
      return response.data;
    } catch (error) {
      logger.error("Error updating extended profile:", error);
      throw new Error(
        error.response?.data?.message || "فشل في تحديث البيانات الموسعة"
      );
    }
  },

  // ✅ إضافة دوال الإعدادات
  async getSettings() {
    try {
      const response = await axiosInstance.get("/profile/settings");
      return response.data;
    } catch (error) {
      logger.error("Error fetching settings:", error);
      throw new Error(error.response?.data?.message || "فشل في جلب الإعدادات");
    }
  },

  async updateSettings(data) {
    try {
      const response = await axiosInstance.put("/profile/settings", data);
      return response.data;
    } catch (error) {
      logger.error("Error updating settings:", error);
      throw new Error(
        error.response?.data?.message || "فشل في تحديث الإعدادات"
      );
    }
  },

  async resetSettings() {
    try {
      const response = await axiosInstance.post("/profile/settings/reset");
      return response.data;
    } catch (error) {
      logger.error("Error resetting settings:", error);
      throw new Error(
        error.response?.data?.message || "فشل في إعادة تعيين الإعدادات"
      );
    }
  },
};

export const notificationService = {
  async getUnreadCount() {
    try {
      logger.debug("Fetching unread notifications count..."); // للتتبع
      const response = await axiosInstance.get("/notifications/unread-count");
      logger.debug("Unread count received:", response.data); // للتتبع
      if (!response.data) {
        throw new Error("فشل في جلب عدد الإشعارات غير المقروءة");
      }
      return response.data;
    } catch (error) {
      logger.error("Error fetching unread notifications count:", error);
      return { count: 0 };
    }
  },

  async getNotifications() {
    try {
      const response = await axiosInstance.get("/notifications");
      if (!response.data) {
        throw new Error("فشل في جلب الإشعارات");
      }
      return response.data;
    } catch (error) {
      logger.error("Error fetching notifications:", error);
      throw new Error("فشل في جلب الإشعارات");
    }
  },

  async markAsRead(notificationId) {
    try {
      const response = await axiosInstance.put(
        `/notifications/${notificationId}/read`
      );
      return response.data;
    } catch (error) {
      logger.error("Error marking notification as read:", error);
      throw new Error("فشل في تحديث حالة الإشعار");
    }
  },

  async markAllAsRead() {
    try {
      const response = await axiosInstance.put("/notifications/mark-all-read");
      return response.data;
    } catch (error) {
      logger.error("Error marking all notifications as read:", error);
      throw new Error("فشل في تحديث حالة الإشعارات");
    }
  },

  async deleteNotification(notificationId) {
    try {
      const response = await axiosInstance.delete(
        `/notifications/${notificationId}`
      );
      return response.data;
    } catch (error) {
      logger.error("Error deleting notification:", error);
      throw new Error("فشل في حذف الإشعار");
    }
  },
};

// خدمة المصاريف
export const expensesService = {
  // جلب جميع المصاريف
  async getExpenses() {
    try {
      const response = await axiosInstance.get("/expenses");
      return response.data.data || [];
    } catch (error) {
      logger.error("Error fetching expenses:", error);
      throw new Error("فشل في جلب المصاريف");
    }
  },

  // جلب مصاريف قضية محددة
  async getExpensesByCaseId(caseId) {
    try {
      const response = await axiosInstance.get(`/expenses/case/${caseId}`);
      return response.data.data || [];
    } catch (error) {
      logger.error("Error fetching case expenses:", error);
      throw new Error("فشل في جلب مصاريف القضية");
    }
  },

  // إنشاء مصروف جديد
  async createExpense(expenseData) {
    try {
      const response = await axiosInstance.post("/expenses", expenseData);
      return response.data;
    } catch (error) {
      logger.error("Error creating expense:", error);
      throw new Error(error.response?.data?.message || "فشل في إضافة المصروف");
    }
  },

  // تحديث مصروف
  async updateExpense(id, expenseData) {
    try {
      const response = await axiosInstance.put(`/expenses/${id}`, expenseData);
      return response.data;
    } catch (error) {
      logger.error("Error updating expense:", error);
      throw new Error(error.response?.data?.message || "فشل في تحديث المصروف");
    }
  },

  // حذف مصروف
  async deleteExpense(id) {
    try {
      const response = await axiosInstance.delete(`/expenses/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error deleting expense:", error);
      throw new Error(error.response?.data?.message || "فشل في حذف المصروف");
    }
  },

  // جلب إجمالي مصاريف قضية
  async getTotalExpenses(caseId) {
    try {
      const response = await axiosInstance.get("/expenses/stats/total", {
        params: { caseId },
      });
      return response.data.data?.total || 0;
    } catch (error) {
      logger.error("Error fetching total expenses:", error);
      return 0;
    }
  },
};

// خدمة الخصوم
export const opponentsService = {
  // جلب جميع الخصوم
  async getAllOpponents(params = {}) {
    try {
      const response = await axiosInstance.get("/opponents", { params });
      return response.data;
    } catch (error) {
      logger.error("Error fetching opponents:", error);
      throw new Error(error.response?.data?.message || "فشل في جلب الخصوم");
    }
  },

  // جلب خصم بالمعرف
  async getOpponentById(id) {
    try {
      const response = await axiosInstance.get(`/opponents/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching opponent:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب بيانات الخصم"
      );
    }
  },

  // جلب خصوم قضية معينة
  async getOpponentsByCaseId(caseId) {
    try {
      const response = await axiosInstance.get(`/opponents/case/${caseId}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching case opponents:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب خصوم القضية"
      );
    }
  },

  // إنشاء خصم جديد
  async createOpponent(opponentData) {
    try {
      const response = await axiosInstance.post("/opponents", opponentData);
      return response.data;
    } catch (error) {
      logger.error("Error creating opponent:", error);
      throw new Error(error.response?.data?.message || "فشل في إنشاء الخصم");
    }
  },

  // تحديث خصم
  async updateOpponent(id, opponentData) {
    try {
      const response = await axiosInstance.put(
        `/opponents/${id}`,
        opponentData
      );
      return response.data;
    } catch (error) {
      logger.error("Error updating opponent:", error);
      throw new Error(error.response?.data?.message || "فشل في تحديث الخصم");
    }
  },

  // حذف خصم
  async deleteOpponent(id) {
    try {
      const response = await axiosInstance.delete(`/opponents/${id}`);
      return response.data;
    } catch (error) {
      logger.error("Error deleting opponent:", error);
      throw new Error(error.response?.data?.message || "فشل في حذف الخصم");
    }
  },

  // تعيين خصم رئيسي للقضية
  async setPrimaryOpponent(caseId, opponentId) {
    try {
      const response = await axiosInstance.put(
        `/opponents/case/${caseId}/primary/${opponentId}`
      );
      return response.data;
    } catch (error) {
      logger.error("Error setting primary opponent:", error);
      throw new Error(
        error.response?.data?.message || "فشل في تعيين الخصم الرئيسي"
      );
    }
  },

  // جلب إحصائيات الخصوم
  async getOpponentsStats(caseId = null) {
    try {
      const params = caseId ? { caseId } : {};
      const response = await axiosInstance.get("/opponents/stats", { params });
      return response.data;
    } catch (error) {
      logger.error("Error fetching opponents stats:", error);
      throw new Error(
        error.response?.data?.message || "فشل في جلب إحصائيات الخصوم"
      );
    }
  },

  // البحث في الخصوم
  async searchOpponents(searchTerm, params = {}) {
    try {
      const response = await axiosInstance.get("/opponents/search", {
        params: { q: searchTerm, ...params },
      });
      return response.data;
    } catch (error) {
      logger.error("Error searching opponents:", error);
      throw new Error(
        error.response?.data?.message || "فشل في البحث عن الخصوم"
      );
    }
  },
};

export const usersService = {
  // جلب جميع المستخدمين
  async getUsers() {
    try {
      const response = await axiosInstance.get("/users");
      return response.data.users || [];
    } catch (error) {
      logger.error("Error fetching users:", error);
      throw new Error("فشل في جلب قائمة المستخدمين");
    }
  },

  // إنشاء مستخدم جديد
  async createUser(userData) {
    try {
      const response = await axiosInstance.post("/users", userData);
      return response.data;
    } catch (error) {
      logger.error("Error creating user:", error);
      throw new Error(error.response?.data?.error || "فشل في إنشاء المستخدم");
    }
  },

  // تحديث بيانات المستخدم
  async updateUser(id, userData) {
    try {
      const response = await axiosInstance.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      logger.error("Error updating user:", error);
      throw new Error(error.response?.data?.error || "فشل في تحديث بيانات المستخدم");
    }
  },

  // تحديث حالة المستخدم
  async updateUserStatus(id, status) {
    try {
      const response = await axiosInstance.patch(`/users/${id}/status`, { status });
      return response.data;
    } catch (error) {
      logger.error("Error updating user status:", error);
      throw new Error(error.response?.data?.error || "فشل في تحديث حالة المستخدم");
    }
  },

  // الحصول على صلاحيات مستخدم معين
  async getUserPermissions(userId) {
    try {
      const response = await axiosInstance.get(`/users/${userId}/permissions`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching user permissions:", error);
      throw new Error(error.response?.data?.error || "فشل في جلب صلاحيات المستخدم");
    }
  },

  // تحديث صلاحيات مستخدم معين
  async updateUserPermissions(userId, permissions) {
    try {
      const response = await axiosInstance.put(`/users/${userId}/permissions`, { permissions });
      return response.data;
    } catch (error) {
      logger.error("Error updating user permissions:", error);
      throw new Error(error.response?.data?.error || "فشل في تحديث صلاحيات المستخدم");
    }
  },

  // إعادة تعيين صلاحيات المستخدم إلى الافتراضية
  async resetUserPermissions(userId) {
    try {
      const response = await axiosInstance.post(`/users/${userId}/permissions/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting user permissions:", error);
      throw new Error(error.response?.data?.error || "فشل في إعادة تعيين الصلاحيات");
    }
  },
};

// إنشاء كائن apiService يجمع جميع الخدمات
const apiService = {
  // الخدمات الموجودة
  dashboard: dashboardService,
  cases: casesService,
  auth: authService,
  clients: clientsService,
  documents: documentsService,
  sessions: sessionsService,
  payments: paymentsService,
  expenses: expensesService,
  profile: profileService,
  notifications: notificationService,
  // إضافة خدمة الخصوم
  opponents: opponentsService,
  // إضافة خدمة المستخدمين
  users: usersService,

  // دوال عامة
  formatDate,

  // دوال مباشرة للتعامل مع الـ API
  get: async (url, config = {}) => {
    try {
      const response = await axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      console.error("API GET Error:", error);
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      console.error("API POST Error:", error);
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      console.error("API PUT Error:", error);
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      console.error("API DELETE Error:", error);
      throw error;
    }
  },
};

export default apiService;
